import { useEffect, useRef } from 'react';
import './Confirm.css';
import { Button } from 'nhsuk-react-components';

interface ConfirmProps {
    title: string;
    message: string;
    onYes: () => void;
    onNo?: () => void;
    yesText?: string;
    noText?: string;
}

function Confirm({
  title,
  message,
  onYes,
  onNo,
  yesText = 'Yes',
  noText = 'No',
}: ConfirmProps) {
  const confirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (confirmRef.current) {
      confirmRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.key === 'Escape' || event.key === 'Enter') && onNo) {
        onNo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNo]);

  return (
    <>
      <div className="nhs-confirm-overlay" data-testid="confirm-overlay" />
      <div
        ref={confirmRef}
        className="nhs-confirm"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        data-testid="confirm-dialog"
      >
        <h2 className="nhs-confirm__title" data-testid="confirm-title">{title}</h2>
        <div className="nhs-confirm__divider" />
        <p className="nhs-confirm__message" data-testid="confirm-message">
          {message}
        </p>
        <div
          className="nhs-confirm__button-container"
          aria-label="Action buttons"
          data-testid="confirm-button-container"
        >
          <Button
            onClick={onYes}
            aria-label={yesText}
            data-testid="confirm-yes-button"
          >
            {yesText}
          </Button>
          {onNo ? (
            <Button
              secondary
              onClick={onNo}
              aria-label={noText}
              data-testid="confirm-no-button"
            >
              {noText}
            </Button>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default Confirm;
